// src/services/hauliers/hauliersService.js
import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled hauliers (keyset pagination, sort, filter).
 * Backend: POST api/Hauliers/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledHauliers({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'name',
    sortDir: sortDir ?? 'asc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
  };

  const { data } = await axios.post('/Hauliers/queryEnabled', body, { signal });
  return data;
}

// GET enabled, paginated (kept for route loader / lookups)
export async function getHauliersEnabled({
  search = '',
  pageSize = 50,
  lastId = null,
  searchFields = null,
  signal = null,
} = {}) {
  const params = { search, pageSize, lastId };
  if (Array.isArray(searchFields) && searchFields.length) {
    params.searchFields = searchFields;
  }
  const { data } = await axios.get('/Hauliers/getEnabled', {
    params,
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat', skipNulls: true }),
    signal,
  });
  return data; // { items, lastId, hasMore }
}

// GET by id
export async function getHaulierById(id, signal = null) {
  const { data } = await axios.get(`/Hauliers/${id}`, { signal });
  return data;
}

// POST create
export async function createHaulier(payload, signal = null) {
  const { data } = await axios.post('/Hauliers', payload, { signal });
  return data;
}

// PUT update
export async function updateHaulier(id, payload, signal = null) {
  await axios.put(`/Hauliers/${id}`, payload, { signal });
  return true;
}

export async function deleteHaulier(id, signal = null) {
  const { data } = await axios.delete(`/Hauliers/${id}`, { signal });
  return data;
}

export async function restoreHaulier(id, signal = null) {
  await axios.post(`/Hauliers/${id}/restore`, null, { signal });
  return true;
}

/**
 * Fetch enabled hauliers by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of haulier IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to hauliers data
 */
export async function getEnabledHauliersByIds(ids, signal = null) {
  if (!ids || ids.length === 0) return { items: [] };
  const res = await axios.post(
    "/Hauliers/byIdsEnabled",
    { filterModel: { items: [], logicOperator: 'and' } },
    {
      params: { ids },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      signal,
    }
  );
  return res.data;
}

/**
 * Fetch lightweight haulier data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, name, erpCode, permitNumber for display purposes.
 * @param {number[]} ids - Array of haulier IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight haulier objects
 */
export async function getEnabledHauliersByIdsLightweight(ids, signal = null) {
  if (!ids || ids.length === 0) return [];
  const res = await axios.post(
    "/Hauliers/byIdsWeighingConsole",
    ids,
    { signal }
  );
  return res.data; // Returns array directly: [{ id, name, erpCode, permitNumber }]
}
